import { z } from 'zod';
import { costOfUsd, MODEL_POLICY, ModelIdSchema, TokenUsageSchema } from './model-policy';

/**
 * What the provider did when asked, and what that is still allowed to mean
 * (docs/DECISIONS.md ZD-077).
 *
 * ## The one row of ZD-051 §3 with no mechanism
 *
 * Three things cover three layers. A cassette replay covers **our code given a
 * fixed provider response**, every commit. `provider-anthropic.test.ts` covers
 * **our use of the SDK**, every commit, driving the genuine client against a
 * local server speaking the real wire format. The third row — **the provider and
 * the model** — had nothing, because `cassetteProvider` *hashes* a request and
 * never validates it: a green replay says the request is unchanged and says
 * nothing whatever about whether it is one the provider would accept. An API
 * that removed a parameter would leave every cassette hitting and every live
 * call returning a 400, which is not hypothetical — it is exactly ZD-050 §12's
 * `temperature` removal, and it is how this system learned that a documented
 * reproducibility anchor had disappeared.
 *
 * ## A decoupling, not a new capability, and saying so is the point
 *
 * A live call is not new here. Three recording sessions have made 142 of them —
 * `sds-04n` (62 requests over two passes, $2.4759), `sds-05m` (40, $0.3427) and
 * `sds-05q` (40, $0.3225) — and **each proved the provider still accepts our
 * requests as a side effect of paying for a recording**. What was missing is a
 * way to ask that question *on its own*. Until now the only route to it cost
 * $0.32–$2.48 and re-rolled every pinned verdict the oracle artefacts carry,
 * because one tool writes cassettes and a run artefact over the same calls
 * (ZD-051 §4). So a session that merely wanted to know whether the wire still
 * works had to buy a recording it did not want, and the honest consequence is
 * that nobody asked: the last such check was whichever recording happened to
 * run most recently, and nothing anywhere said when that was.
 *
 * This asks for **one call**. That is the whole of what it adds.
 *
 * ## What it does not claim, in the same register as what it does
 *
 * **It is not evidence about reproducibility, and the measurement saying so was
 * in the repository before this file existed.** Across the only two independent
 * live passes that exist — `sds-04n`'s two identity passes, and the 21
 * byte-identical requests shared by the `sds-05m` and `sds-05q` contextualiser
 * recordings — **62 of 62 structured decisions agree and 0 of 21 comparable
 * texts are identical** (ZD-077 §2). One call cannot see either half of that. A
 * green verdict here says the wire works; it says nothing about whether two runs
 * would agree, and nothing about whether any answer is *right*.
 *
 * **It is also not the human gate, and the two must not be collapsed under one
 * word.** ZD-061's gate refuses to release *findings*, and an acceptance there
 * means *this may be shown to the client*, not *this finding is correct* — a
 * reviewer cannot detect that a provider changed under them, and ZD-061 §4 lists
 * what they cannot catch. This refuses to trust *artefacts about a provider* and
 * says nothing about whether any finding is true. Different layers, different
 * failures, neither substituting for the other: the same shape as `Slot.origin`
 * against `Provenance.asserted_by` (ZD-046 §7), which is the pair this codebase
 * already keeps apart for the same reason.
 *
 * ## The stored half is what a provider said; the verdict is derived on read
 *
 * ZD-065's rule applied rather than restated: a stored artefact may not hold a
 * figure our own code derives. So an observation records the provider's own
 * answers — accepted or refused, which version answered, how many tokens — and
 * **`passed` is not among them**. `conformanceVerdict` computes it against
 * `MODEL_POLICY` on read, which is what makes a committed observation a live
 * guard rather than a souvenir: edit `current_version` to something the provider
 * did not return, and the build reddens in the session that edited it.
 *
 * **And there is no money column and no rates stamp, which is ZD-066 working
 * rather than being ignored.** That entry stamps a stored figure *computed from
 * a versioned table*, and the case for it was that `identity-run.json`'s second
 * pass has tokens in no cassette, so its `usd` cannot be recomputed. Nothing
 * here is in that position: tokens are stored, `conformanceCostUsd` prices them
 * on read, and adding `appliedRates` beside them would be a stamp on a figure
 * that does not exist.
 */

/** The provider answered and the answer was usable. */
export const AcceptedProbeSchema = z.strictObject({
  outcome: z.literal('accepted'),
  /** `cassetteKey` over the request sent, so a probe names what it asked. */
  request_hash: z.string(),
  /** The version the provider says answered — the claim `MODEL_POLICY` pins. */
  model_version: z.string(),
  usage: TokenUsageSchema,
  /**
   * Whether the completion satisfied the calling worker's output schema.
   *
   * **Ours rather than the provider's, and anchored for that reason.** Every
   * other field here is something the provider asserted; this is a judgement our
   * code made over bytes this artefact deliberately does not keep — keeping them
   * would make it a cassette, and a cassette re-rolls when a prompt or a schema
   * moves, which is the coupling this file exists to avoid. So it is anchored
   * the way `identity-run.json` anchors a verdict: by `probe_source`, naming the
   * worker and prompt version the claim was true of, and by `checked_at`.
   */
  parsed: z.boolean(),
});

/** The provider refused, or could not be reached. */
export const RejectedProbeSchema = z.strictObject({
  outcome: z.literal('rejected'),
  request_hash: z.string(),
  /**
   * Which of the adapter's two typed failures this was.
   *
   * **Kept because a refusal for the wrong reason is not a refusal.** The
   * negative probe exists to observe that the provider still rejects what
   * `MODEL_POLICY` says it rejects; a 5xx or a dropped connection also produces
   * a failed call and evidences nothing about the parameter. That is the
   * mutation harness's `red, wrong reason` rule (ZD-042 §3) arriving at a live
   * call, and folding the two together would be ZD-041 §3's one value carrying
   * two causes, which the caller then re-derives wrongly.
   */
  classification: z.enum(['rejected', 'unavailable']),
  detail: z.string(),
});

export const ConformanceProbeSchema = z.discriminatedUnion('outcome', [
  AcceptedProbeSchema,
  RejectedProbeSchema,
]);

export const ConformanceObservationSchema = z.strictObject({
  /** `YYYY-MM-DD`. What a reader checks when asking how old the evidence is. */
  checked_at: z.string(),
  model: ModelIdSchema,
  /**
   * Which worker's request shape was sent, and under which prompt.
   *
   * The wire shape is the same for every worker — `model`, `max_tokens`,
   * `system`, `messages`, and `temperature` omitted where the model removed it —
   * so one probe covers all of them for the *accepted* question. It does **not**
   * cover `parsed`, which is per worker and per prompt, and this field is what
   * stops the narrower claim being read as the wider one.
   */
  probe_source: z.strictObject({ worker: z.string(), prompt_version: z.string() }),
  /** The request the shipping path builds. The provider must accept it. */
  accepts: ConformanceProbeSchema,
  /**
   * The request `MODEL_POLICY` says the provider refuses. It must be rejected.
   *
   * **This is the half that proves the check can go red.** A gate observed only
   * passing documents nothing — break-it-to-check-it, applied to a live provider
   * rather than to a guard. `provider-anthropic.test.ts` classifies a 4xx
   * correctly against a local server *pretending* to be the provider; only this
   * says the provider itself still refuses what we believe it refuses, which
   * turns `accepts_temperature: false` from somebody reading documentation on a
   * date into an observation with a request hash beside it.
   */
  refuses: ConformanceProbeSchema,
});

export type AcceptedProbe = z.infer<typeof AcceptedProbeSchema>;
export type RejectedProbe = z.infer<typeof RejectedProbeSchema>;
export type ConformanceProbe = z.infer<typeof ConformanceProbeSchema>;
export type ConformanceObservation = z.infer<typeof ConformanceObservationSchema>;

/**
 * `{passed, failures}` rather than a boolean, for `reproducibilityGate`'s
 * reason: a guard whose whole answer is one value tells a reader *that*
 * something is wrong and not *which* of several unrelated things — and these six
 * want six different fixes.
 */
export interface ConformanceVerdict {
  passed: boolean;
  failures: string[];
}

/**
 * The verdict, derived from what the provider said and what the policy claims.
 *
 * **Six clauses, and the input each one catches is named** — because a clause
 * written for a case an earlier clause has already rejected is dead code that
 * reads as a protection, sits directly beneath the thing that kills it, and
 * reports `STAYED GREEN` when somebody breaks it (`sds-06`'s `approver_decided`,
 * ZD-068 §3). The arithmetic, done here rather than assumed:
 *
 *  1. the provider **refused** a request the shipping path builds;
 *  2. it accepted, and a **different version** answered than the policy pins;
 *  3. it accepted under the pinned version, and the completion **failed the
 *     worker's output schema**;
 *  4. all of that held and the **usage fields report nothing**, which is what a
 *     renamed wire field looks like after the adapter maps the body by name;
 *  5. the provider **accepted** a request the policy says it refuses;
 *  6. the negative probe failed as a **transport failure** rather than a
 *     refusal — red for the wrong reason.
 *
 * 1→4 chain over `accepts` and 5→6 over `refuses`; the two groups are
 * independent, and each clause has an input no earlier clause consumes.
 */
export function conformanceVerdict(observation: ConformanceObservation): ConformanceVerdict {
  const failures: string[] = [];
  const policy = MODEL_POLICY[observation.model];
  const { accepts, refuses } = observation;

  if (accepts.outcome !== 'accepted') {
    failures.push(
      `the provider refused a request the shipping path builds (${accepts.classification}): ` +
        `${accepts.detail}. No cassette replay can see this, because it hashes a request ` +
        'rather than validating it',
    );
  } else if (accepts.model_version !== policy.current_version) {
    failures.push(
      `MODEL_POLICY pins ${observation.model} at current_version ` +
        `${policy.current_version ?? 'null'} and the provider answered as ` +
        `${accepts.model_version}, so every cassette captured against the pinned version is ` +
        'evidence about a model that is no longer the one being routed to',
    );
  } else if (!accepts.parsed) {
    failures.push(
      `the completion did not satisfy ${observation.probe_source.worker}'s output schema at ` +
        `prompt ${observation.probe_source.prompt_version}, although the provider accepted ` +
        'the request',
    );
  } else if (accepts.usage.input_tokens <= 0 || accepts.usage.output_tokens <= 0) {
    /*
     * A call that returned a parseable completion consumed tokens. Zero means
     * the field the adapter reads is not the field the provider now writes: the
     * body is mapped by name and the cache fields are coerced with `?? 0`, so a
     * rename arrives as a *free call* rather than as an error. Every spend
     * ceiling and every cost row is computed from these two numbers, so the
     * failure would be a budget that never binds — ZD-046 §3's halt-not-degrade
     * defeated by arithmetic rather than by a decision.
     */
    failures.push(
      `the provider reported ${String(accepts.usage.input_tokens)} input and ` +
        `${String(accepts.usage.output_tokens)} output tokens for a call that returned a ` +
        'parseable completion, so the fields the cost row bills from have moved',
    );
  }

  if (refuses.outcome === 'accepted') {
    failures.push(
      'the provider accepted the request MODEL_POLICY says it refuses, so the negative probe ' +
        'evidences nothing and this check has never been observed able to fail',
    );
  } else if (refuses.classification !== 'rejected') {
    failures.push(
      `the negative probe failed as ${refuses.classification} rather than as a refusal: ` +
        `${refuses.detail}. A transport failure is not evidence that the provider still ` +
        'refuses what the policy says it refuses',
    );
  }

  return { passed: failures.length === 0, failures };
}

/**
 * What the observed call cost, priced at today's table.
 *
 * Derived on read and **not stored**, which is ZD-046 §3's money rule rather
 * than an omission: tokens are the fact and the price is a versioned lookup.
 * `identity-run.json` keeps a `usd` because half its tokens are in no cassette
 * and the figure would otherwise be lost (ZD-066 §1); this artefact has no such
 * half, so there is nothing to stamp and nothing that can go stale.
 */
export function conformanceCostUsd(observation: ConformanceObservation): number {
  const { accepts } = observation;
  return accepts.outcome === 'accepted' ? costOfUsd(observation.model, accepts.usage) : 0;
}

/**
 * Whole days between the observation and `now`, or `null` where the date will
 * not parse.
 *
 * **Reported, never gated**, and the reason is a rule this repository learned
 * expensively: a wall-clock threshold reddens a build for a reason nobody
 * changed, and a guard people re-run until it passes has stopped being a guard
 * (ZD-046 §6's own argument for cassettes; ZD-046 §9's `source-hygiene` budget
 * one layer along). What a reader needs is the age *beside* the verdict, so a
 * green result cannot be read as *checked recently* when it means *checked
 * once*.
 *
 * `now` is a parameter rather than a `Date.now()` inside, so that nothing here
 * can be tested by a clock — a test asserting on the real age would be green
 * today and red on a quiet fortnight, which is the flake this rule exists to
 * refuse.
 */
export function conformanceAgeDays(observation: ConformanceObservation, now: Date): number | null {
  const checked = Date.parse(`${observation.checked_at}T00:00:00.000Z`);
  if (Number.isNaN(checked)) return null;
  return Math.floor((now.getTime() - checked) / 86_400_000);
}
